import React, { useState } from 'react';
import { motion } from 'framer-motion';
import ReactCalendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import { dailyCapacity, blockedDays } from '../data/mockData';

interface CalendarProps {
  onDateSelect: (date: Date) => void;
}

const Calendar: React.FC<CalendarProps> = ({ onDateSelect }) => {
  const [date, setDate] = useState<Date>(new Date());
  
  const handleDateChange = (newDate: Date) => {
    setDate(newDate);
    onDateSelect(newDate);
  };

  // Formatage des dates pour permettre la comparaison
  const formatDate = (date: Date): string => {
    return date.toISOString().split('T')[0];
  };

  // Vérifier si une date est bloquée
  const isDateBlocked = (date: Date): boolean => {
    const formattedDate = formatDate(date);
    return blockedDays.some(day => day.date === formattedDate);
  };

  // Obtenir la raison du blocage
  const getBlockReason = (date: Date): string | null => {
    const formattedDate = formatDate(date);
    const blockedDay = blockedDays.find(day => day.date === formattedDate);
    return blockedDay ? blockedDay.reason : null;
  };

  // Obtenir les informations de capacité pour une date
  const getCapacityInfo = (date: Date) => {
    const formattedDate = formatDate(date);
    return dailyCapacity.find(day => day.date === formattedDate);
  };

  // Personnalisation du rendu des tuiles du calendrier
  const tileContent = ({ date, view }: { date: Date; view: string }) => {
    if (view !== 'month') return null;
    
    const capacityInfo = getCapacityInfo(date);
    const isBlocked = isDateBlocked(date);
    
    // Ne rien afficher pour les jours sans information ou passés
    if ((!capacityInfo && !isBlocked) || date < new Date()) return null;
    
    return (
      <div className="text-xs mt-1">
        {isBlocked ? (
          <div className="bg-red-100 text-red-700 rounded-sm py-1 px-1 whitespace-nowrap overflow-hidden text-ellipsis">
            Bloqué
          </div>
        ) : capacityInfo ? (
          <div className={`
            ${capacityInfo.currentOccupancy >= capacityInfo.maxCapacity 
              ? 'bg-red-100 text-red-700' 
              : capacityInfo.currentOccupancy >= capacityInfo.maxCapacity * 0.8 
                ? 'bg-yellow-100 text-yellow-700' 
                : 'bg-green-100 text-green-700'
            } rounded-sm py-1 px-1
          `}>
            {capacityInfo.currentOccupancy}/{capacityInfo.maxCapacity}
          </div>
        ) : null}
      </div>
    );
  };

  // Style personnalisé pour les tuiles du calendrier
  const tileClassName = ({ date, view }: { date: Date; view: string }) => {
    if (view !== 'month') return '';
    
    if (isDateBlocked(date)) {
      return 'blocked-date';
    }

    const capacityInfo = getCapacityInfo(date);
    if (!capacityInfo) return '';
    
    if (capacityInfo.currentOccupancy >= capacityInfo.maxCapacity) {
      return 'full-capacity';
    } else if (capacityInfo.currentOccupancy >= capacityInfo.maxCapacity * 0.8) {
      return 'high-capacity';
    } else {
      return 'low-capacity';
    }
  };

  return (
    <motion.div
      className="bg-white rounded-lg shadow-lg p-6"
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      viewport={{ once: true }}
    >
      <h3 className="text-xl font-semibold text-gray-800 mb-4">Calendrier des réservations</h3>
      
      <div className="calendar-wrapper">
        <style jsx global>{`
          .react-calendar {
            width: 100%;
            border: none;
            border-radius: 8px;
            font-family: 'Inter', sans-serif;
          }
          .react-calendar__tile {
            padding: 10px 6px;
            position: relative;
            height: 70px;
          }
          .react-calendar__tile--active {
            background: #90cdf4;
            color: white;
          }
          .react-calendar__tile--now {
            background: #ebf8ff;
          }
          .react-calendar__tile:enabled:hover,
          .react-calendar__tile:enabled:focus {
            background-color: #e6f7ff;
          }
          .react-calendar__navigation button:enabled:hover,
          .react-calendar__navigation button:enabled:focus {
            background-color: #e6f7ff;
          }
          .react-calendar__month-view__days__day--weekend {
            color: #f56565;
          }
          .blocked-date {
            background-color: #fff5f5;
            color: #c53030;
            position: relative;
          }
          .full-capacity {
            background-color: #fff5f5;
          }
          .high-capacity {
            background-color: #fffaf0;
          }
          .low-capacity {
            background-color: #f0fff4;
          }
        `}</style>
        
        <ReactCalendar 
          onChange={handleDateChange}
          value={date}
          tileContent={tileContent}
          tileClassName={tileClassName}
        />
      </div>
      
      <div className="mt-4 flex flex-wrap gap-2">
        <div className="flex items-center">
          <div className="w-3 h-3 bg-green-100 mr-1"></div>
          <span className="text-xs text-gray-600">Disponible</span>
        </div>
        <div className="flex items-center">
          <div className="w-3 h-3 bg-yellow-100 mr-1"></div>
          <span className="text-xs text-gray-600">Bientôt complet</span>
        </div>
        <div className="flex items-center">
          <div className="w-3 h-3 bg-red-100 mr-1"></div>
          <span className="text-xs text-gray-600">Complet</span>
        </div>
        <div className="flex items-center">
          <div className="w-3 h-3 bg-red-100 mr-1"></div>
          <span className="text-xs text-gray-600">Bloqué</span>
        </div>
      </div>
      
      {isDateBlocked(date) && (
        <motion.div 
          className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm"
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          transition={{ duration: 0.3 }}
        >
          <strong>Date bloquée:</strong> {getBlockReason(date)}
        </motion.div>
      )}
    </motion.div>
  );
};

export default Calendar;
